import * as React from "react";

import { cn } from "../lib/utils";

/**
 * v2 AI marker (redesign phase 5, #455): THE one mark for AI in the product.
 * Replaces lucide `Sparkles`, the v1 `GradientSparkle` and hand-typed purple
 * (#D571E5) marks (break #102). Storybook only until implementation; see
 * docs/design-library/04-implementation-notes.md.
 *
 * An `IconComponent`: it drops into `<Icon>`, Badge `icon`, Button
 * `leadingIcon`, IconButton `icon` and the Tabs / Nav icon slots, and takes the
 * same props. `strokeWidth` is accepted and ignored: the glyph is filled.
 *
 * DECORATIVE, always (break #103). Meaning comes from visible text next to it
 * or from the control's accessible name, never from the marker alone, so it is
 * `aria-hidden` and excluded from the contrast checks (theme-v2-contrast.ts).
 *
 * Legibility: cyan and yellow nearly vanish on white (1.46:1, ~1.1:1), so
 *   - the glyph is FILLED (about three times the ink of a 1.75 stroke at 12px),
 *     with a 1px round-joined edge in the same paint that softens the tips;
 *   - the gradient runs bottom-left → top-right across the whole icon, so the
 *     large star (the silhouette) is purple, and cyan → yellow land on its
 *     upper tips and on the small star;
 *   - light mode starts at Purple 7 (`--ai-marker-start`, #8E4799, 6.0:1 on
 *     white); dark starts at Purple 5 like the brand gradient.
 * Order stays Purple → Cyan → Yellow (DESIGN.md "Gradients"); the stops are
 * the theme tokens, no hex here.
 *
 * Usage (see the AI marker story): on outline / ghost Buttons and IconButtons,
 * NEVER on the blue default fill; Badge `tone="ai"` carries it by default; as
 * the label of AI-generated content. One marker per AI region, not as
 * decoration.
 */

export interface AiMarkerProps {
  /** 12 with badges and caption text, 16 in controls and next to 14px text, 20 with headings. */
  size?: number | string;
  /** Ignored: the marker is a filled glyph. Accepted so it fits `IconComponent`. */
  strokeWidth?: number | string;
  className?: string;
  /** Always rendered as true: the marker is decorative. */
  "aria-hidden"?: boolean | "true" | "false";
}

/** Large four-point star (lower left) + small one (upper right), on the 24 grid. */
const PATH =
  "M10 5Q12.2 11.8 19 14Q12.2 16.2 10 23Q7.8 16.2 1 14Q7.8 11.8 10 5Z" +
  "M19.5 1Q20.4 3.6 23 4.5Q20.4 5.4 19.5 8Q18.6 5.4 16 4.5Q18.6 3.6 19.5 1Z";

export function AiMarker({ size = 16, className }: AiMarkerProps) {
  // React's ids contain characters (":", "«", "»") that don't resolve inside
  // an SVG `url(#…)` reference, which would paint the glyph as nothing.
  const id = "ai-marker-" + React.useId().replace(/[^A-Za-z0-9_-]/g, "");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      data-slot="ai-marker"
      className={cn("shrink-0", className)}
    >
      <defs>
        {/* User space: one gradient across both stars, not one per shape. */}
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="2" y1="22" x2="22" y2="2">
          <stop offset="0.2" style={{ stopColor: "var(--ai-marker-start, var(--gradient-from))" }} />
          <stop offset="0.5" style={{ stopColor: "var(--gradient-from)" }} />
          <stop offset="0.78" style={{ stopColor: "var(--gradient-via)" }} />
          <stop offset="0.96" style={{ stopColor: "var(--gradient-to)" }} />
        </linearGradient>
      </defs>
      <path
        d={PATH}
        fill={`url(#${id})`}
        stroke={`url(#${id})`}
        strokeWidth={1}
        strokeLinejoin="round"
        // Windows high contrast drops gradients; fall back to the text colour.
        className="forced-colors:fill-current forced-colors:stroke-current"
      />
    </svg>
  );
}
